import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
  Database,
  Upload,
  Plus,
  Search,
  Check,
  FileText,
  Trash2,
  FileCode,
} from "lucide-react";

import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../components/ui/pagination";

import { TabNavigation } from "../../../components/ui/tab-navigation";
import AddNewDatasetPage from "../../AddNewDatasetPage";
import TiptapEditor from "../../../components/ui/TiptapEditor";
import { useEnhancedDatasetsQuery } from "../../../api/createDatasetApi";
import { ProjectAssetsStepProps } from "../types";

const ProjectAssetsStep: React.FC<ProjectAssetsStepProps> = React.memo(
  ({ formData, handleInputChange }) => {
    const [activeTab, setActiveTab] = useState("select");
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddDatasetModal, setShowAddDatasetModal] = useState(false);
    const itemsPerPage = 6;

    // Fetch datasets from repository
    const {
      data: datasetsResponse,
      isLoading: datasetsLoading,
      refetch: refetchDatasets,
    } = useEnhancedDatasetsQuery(
      {
        page: currentPage,
        limit: itemsPerPage,
        ...(searchQuery && { search: searchQuery }),
      },
      { enabled: true },
    );

    // Dropzone for file upload
    const { getRootProps, getInputProps, isDragActive, isDragReject } =
      useDropzone({
        onDrop: (acceptedFiles: File[]) => {
          const newDatasets = acceptedFiles.map((file) => {
            const kb = Math.round(file.size / 1024);
            const sizeStr =
              kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(2)} MB`;
            return {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
              name: file.name,
              file: file,
              size: sizeStr,
            };
          });
          handleInputChange("datasets", [
            ...(formData.datasets || []),
            ...newDatasets,
          ]);
          if (acceptedFiles.length === 1) {
            toast.success(`Added ${acceptedFiles[0].name}`);
          } else {
            toast.success(`Added ${acceptedFiles.length} files`);
          }
        },
        accept: {
          "text/csv": [".csv"],
          "application/json": [".json"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
          "application/octet-stream": [".parquet"],
        },
        multiple: true,
      });

    // Dropzone for notebook/module files
    const {
      getRootProps: getNotebookRootProps,
      getInputProps: getNotebookInputProps,
      isDragActive: isNotebookDragActive,
      isDragReject: isNotebookDragReject,
    } = useDropzone({
      onDrop: (acceptedFiles: File[]) => {
        const newNotebookFiles = acceptedFiles.map((file) => {
          const kb = Math.round(file.size / 1024);
          const sizeStr =
            kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(2)} MB`;
          return {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            name: file.name,
            file: file,
            size: sizeStr,
          };
        });
        handleInputChange("notebookFiles", [
          ...(formData.notebookFiles || []),
          ...newNotebookFiles,
        ]);
        if (acceptedFiles.length === 1) {
          toast.success(`Added ${acceptedFiles[0].name}`);
        } else {
          toast.success(`Added ${acceptedFiles.length} files`);
        }
      },
      accept: {
        "text/x-python": [".py"],
        "application/x-ipynb+json": [".ipynb"],
      },
      multiple: true,
    });

    const handleRemoveNotebookFile = (fileId: string, fileName: string) => {
      handleInputChange(
        "notebookFiles",
        (formData.notebookFiles || []).filter((f: any) => f.id !== fileId),
      );
      toast.success(`Removed ${fileName}`);
    };

    const handleRemoveDataset = (datasetId: string, datasetName: string) => {
      handleInputChange(
        "datasets",
        formData.datasets.filter((ds: any) => ds.id !== datasetId),
      );
      // If it was an existing dataset, we should also remove it from selectedDatasetIds
      if (formData.selectedDatasetIds?.includes(datasetId)) {
        handleInputChange(
          "selectedDatasetIds",
          formData.selectedDatasetIds.filter((id: string) => id !== datasetId),
        );
      }
      toast.success(`Removed ${datasetName}`);
    };

    // Filter datasets (exclude PROJECT visibility)
    const currentDatasets =
      datasetsResponse?.datasets?.filter(
        (ds: any) => ds.visibility !== "PROJECT",
      ) || [];

    // Pagination
    const totalItems =
      datasetsResponse?.pagination?.total || currentDatasets.length;
    const totalPages =
      datasetsResponse?.pagination?.total_pages ||
      Math.ceil(totalItems / itemsPerPage);

    const getVisiblePages = () => {
      const pages: number[] = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 3; i++) {
            pages.push(i);
          }
        } else if (currentPage >= totalPages - 2) {
          for (let i = totalPages - 2; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(currentPage - 1, currentPage, currentPage + 1);
        }
      }
      return pages;
    };

    const visiblePages = getVisiblePages();

    return (
      <div className="space-y-6">
        {/* Add Dataset Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Add Dataset</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddDatasetModal(true)}
                className="text-blue-600 hover:bg-blue-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create new dataset
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Select dataset or upload files directly
            </p>

            {/* Tab Navigation */}
            <TabNavigation
              tabs={[
                {
                  key: "select",
                  label: "Select Dataset",
                  icon: <Database className="h-4 w-4" />,
                },
                {
                  key: "upload",
                  label: "Upload File",
                  icon: <Upload className="h-4 w-4" />,
                },
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="mb-6"
            />

            {/* Select Dataset Tab */}
            {activeTab === "select" && (
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search file, tag..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10"
                  />
                </div>

                {/* Selected Count */}
                {formData.selectedDatasetIds &&
                  formData.selectedDatasetIds.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-600">
                          {formData.selectedDatasetIds.length} dataset(s)
                          selected
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleInputChange("selectedDatasetIds", [])
                        }
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                      >
                        Clear all
                      </Button>
                    </div>
                  )}

                {/* Dataset Grid */}
                {datasetsLoading ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Loading datasets...</p>
                  </div>
                ) : currentDatasets.length === 0 ? (
                  <div className="text-center py-8">
                    <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-sm text-gray-500">
                      {searchQuery
                        ? "No datasets found matching your search"
                        : "No datasets available"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentDatasets.map((dataset: any) => {
                      const isSelected = formData.selectedDatasetIds?.includes(
                        dataset.id,
                      );
                      return (
                        <div
                          key={dataset.id}
                          onClick={() => {
                            const currentIds =
                              formData.selectedDatasetIds || [];
                            const newIds = isSelected
                              ? currentIds.filter(
                                  (id: string) => id !== dataset.id,
                                )
                              : [...currentIds, dataset.id];
                            handleInputChange("selectedDatasetIds", newIds);
                          }}
                          className={`relative cursor-pointer rounded-lg border-2 transition-all hover:shadow-md ${
                            isSelected
                              ? "border-blue-600 bg-blue-50"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          {/* Checkbox */}
                          <div
                            className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? "bg-blue-600 border-blue-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex items-start p-4 pr-12">
                            {/* Cover Photo */}
                            <div className="w-32 h-28 rounded overflow-hidden shrink-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                              {dataset.coverPhotoUrl ? (
                                <img
                                  src={dataset.coverPhotoUrl}
                                  alt={dataset.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Database className="h-10 w-10 text-gray-400 opacity-50" />
                              )}
                            </div>

                            {/* Info */}
                            <div className="ml-4 flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                                {dataset.title}
                              </h3>
                              <p className="text-xs text-gray-600 mb-2">
                                by{" "}
                                <span className="font-medium">
                                  {dataset.createdBy?.name || "Unknown"}
                                </span>{" "}
                                • Updated on{" "}
                                {new Date(
                                  dataset.updatedAt,
                                ).toLocaleDateString()}
                              </p>
                              {dataset.description && (
                                <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                                  {dataset.description}
                                </p>
                              )}
                              {dataset.tags && dataset.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {dataset.tags
                                    .slice(0, 3)
                                    .map((tag: string, idx: number) => (
                                      <span
                                        key={idx}
                                        className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  {dataset.tags.length > 3 && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                                      +{dataset.tags.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Pagination className="mt-6">
                    <PaginationContent>
                      {/* Previous */}
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              setCurrentPage(currentPage - 1);
                          }}
                          aria-disabled={currentPage === 1}
                          className={
                            currentPage === 1
                              ? "pointer-events-none opacity-50"
                              : ""
                          }
                        />
                      </PaginationItem>

                      {/* First page + ellipsis */}
                      {currentPage > 3 && (
                        <>
                          <PaginationItem>
                            <PaginationLink onClick={() => setCurrentPage(1)}>
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {currentPage > 4 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                        </>
                      )}

                      {/* Visible pages */}
                      {visiblePages.map((pageNum) => (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            isActive={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      {/* Last page + ellipsis */}
                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setCurrentPage(totalPages)}
                            >
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}

                      {/* Next */}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages)
                              setCurrentPage(currentPage + 1);
                          }}
                          aria-disabled={currentPage === totalPages}
                          className={
                            currentPage === totalPages
                              ? "pointer-events-none opacity-50"
                              : ""
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            )}

            {/* Upload File Tab */}
            {activeTab === "upload" && (
              <div className="space-y-4">
                {/* Dropzone */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive && !isDragReject
                      ? "border-blue-500 bg-blue-50"
                      : isDragReject
                        ? "border-red-500 bg-red-50"
                        : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload
                    className={`h-12 w-12 mx-auto mb-4 ${
                      isDragActive ? "text-blue-600" : "text-gray-400"
                    }`}
                  />
                  {isDragReject ? (
                    <p className="text-sm text-red-600 mb-2 font-medium">
                      Invalid file type. Please upload CSV, JSON, XLSX, or
                      Parquet files.
                    </p>
                  ) : isDragActive ? (
                    <p className="text-sm text-blue-600 mb-2 font-medium">
                      Drop the files here...
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium text-blue-600">
                          Click to upload
                        </span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        CSV, JSON, XLSX, or Parquet files
                      </p>
                    </>
                  )}
                </div>

                {/* Uploaded Files List */}
                {formData.datasets && formData.datasets.length > 0 && (
                  <div className="space-y-2">
                    <Label>Uploaded Files</Label>
                    {formData.datasets.map((dataset: any) => {
                      const isExisting =
                        !!dataset.visibility &&
                        dataset.visibility === "PROJECT";
                      return (
                        <div
                          key={dataset.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isExisting
                              ? "bg-blue-50 border-blue-200"
                              : "bg-green-50 border-green-200"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <FileText
                              className={`h-5 w-5 ${isExisting ? "text-blue-600" : "text-green-600"}`}
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">
                                  {dataset.name || dataset.title}
                                </p>
                                {isExisting && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                    Uploaded
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {dataset.size ||
                                  (dataset.s3_keys?.[0]?.size
                                    ? `${(dataset.s3_keys[0].size / 1024).toFixed(2)} KB`
                                    : "Stored in repository")}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {isExisting
                                  ? "Available in sandbox"
                                  : "Ready to upload"}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveDataset(
                                dataset.id,
                                dataset.name || dataset.title,
                              )
                            }
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Notebook or Module Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileCode className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold">Add Notebook or Module</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Upload Python (.py) or Jupyter Notebook (.ipynb) files that will
              be provisioned to student workspaces
            </p>

            {/* Dropzone */}
            <div
              {...getNotebookRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isNotebookDragActive && !isNotebookDragReject
                  ? "border-purple-500 bg-purple-50"
                  : isNotebookDragReject
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300 bg-gray-50 hover:border-purple-400 hover:bg-purple-50"
              }`}
            >
              <input {...getNotebookInputProps()} />
              <FileCode
                className={`h-12 w-12 mx-auto mb-4 ${
                  isNotebookDragActive ? "text-purple-600" : "text-gray-400"
                }`}
              />
              {isNotebookDragReject ? (
                <p className="text-sm text-red-600 mb-2 font-medium">
                  Invalid file type. Please upload .py or .ipynb files only.
                </p>
              ) : isNotebookDragActive ? (
                <p className="text-sm text-purple-600 mb-2 font-medium">
                  Drop the files here...
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium text-purple-600">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    Python (.py) or Jupyter Notebook (.ipynb) files
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Max file size: 50MB per file
                  </p>
                </>
              )}
            </div>

            {/* Uploaded Notebook Files List */}
            {formData.notebookFiles && formData.notebookFiles.length > 0 && (
              <div className="space-y-2 mt-4">
                <Label>Uploaded Notebook Files</Label>
                {formData.notebookFiles.map((file: any) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200"
                  >
                    <div className="flex items-center gap-3">
                      <FileCode className="h-5 w-5 text-purple-600" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{file.name}</p>
                          {file.isExisting && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                              Uploaded
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{file.size}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Will be provisioned to notebooks/ folder
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleRemoveNotebookFile(file.id, file.name)
                      }
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add New Dataset Modal */}
        <AddNewDatasetPage
          isOpen={showAddDatasetModal}
          onClose={() => setShowAddDatasetModal(false)}
          onSuccess={async (newDataset) => {
            try {
              // Refetch datasets to include the new one
              await refetchDatasets();

              // Auto-select the newly created dataset
              if (newDataset && newDataset.id) {
                const updatedIds = [
                  ...(formData.selectedDatasetIds || []),
                  newDataset.id,
                ];
                handleInputChange("selectedDatasetIds", updatedIds);
                toast.success("Dataset created and selected successfully!");
              } else {
                toast.success("Dataset created successfully!");
              }
            } catch (error) {
              toast.warning(
                "Dataset created but there was an issue refreshing the list",
              );
            } finally {
              setShowAddDatasetModal(false);
            }
          }}
        />

        {/* Project Instructions & Notebook */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">
                Project Instructions & Notebook
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Create the initial notebook content and instructions for students
            </p>

            <div>
              <Label htmlFor="readme">Readme.md</Label>
              <div className="mt-1">
                <TiptapEditor
                  value={formData.instructions}
                  onChange={(html) => handleInputChange("instructions", html)}
                  placeholder="Write detailed instructions, objectives, and guidelines for students...&#10;&#10;Include:&#10;- Project overview and goals&#10;- Step-by-step instructions&#10;- Expected outcomes&#10;- Tips and best practices"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This content will be displayed to students in the practice
                project workspace
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  },
);

ProjectAssetsStep.displayName = "ProjectAssetsStep";

export default ProjectAssetsStep;
